import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { HelpCircle, ChevronDown, ChevronUp, ExternalLink } from 'lucide-react';
import { ROUTES } from '../routes';

interface FooterProps {
  onNavigateToTab?: (tab: string) => void;
}

export const Footer: React.FC<FooterProps> = ({ onNavigateToTab }) => {
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  const faqs = [
    {
      q: 'How is UK Take Home Pay calculated in 2025/26?',
      a: 'Take-home pay (net salary) is your gross annual salary minus Income Tax, National Insurance (NI) contributions, workplace pension contributions, and any student loan repayments. The standard Personal Allowance is £12,570 per year (tax-free). Income above £12,570 is taxed at 20% basic rate up to £50,270, 40% higher rate up to £125,140, and 45% additional rate thereafter (or Scottish tax bands if living in Scotland).',
    },
    {
      q: 'What changed for National Insurance in 2024 and 2025?',
      a: 'The Employee Class 1 National Insurance main rate was cut from 10% to 8% in April 2024. Workers pay 8% NI on earnings between the Primary Threshold (£12,570/yr or £1,047.50/mo) and Upper Earnings Limit (£50,270/yr or £4,189/mo), and 2% on earnings above £50,270.',
    },
    {
      q: 'What is Salary Sacrifice and how does it save tax?',
      a: 'Salary Sacrifice is an agreement where you surrender part of your gross salary in exchange for a non-cash benefit, most commonly a workplace pension contribution, electric vehicle, or cycle-to-work scheme. Because the sacrifice reduces your gross pay before Tax and NI are calculated, you pay less Income Tax AND less National Insurance!',
    },
    {
      q: 'How does the £100,000 Personal Allowance Taper Trap work?',
      a: 'If your taxable income exceeds £100,000, your £12,570 Personal Allowance is reduced by £1 for every £2 of income above £100,000 until it reaches zero at £125,140. This creates an effective 60% marginal income tax rate on earnings between £100k and £125.14k (62% including 2% NI).',
    },
    {
      q: 'How are Student Loans deducted from payslips?',
      a: 'Student loan repayments are automatically calculated by your employer through PAYE once your gross earnings cross the plan threshold: Plan 1 (£24,990/yr), Plan 2 (£27,295/yr), Plan 4 Scotland (£31,395/yr), Plan 5 (£25,000/yr) at 9% above the threshold, or Postgraduate loans at 6% above £21,000/yr.',
    },
  ];

  return (
    <footer className="bg-zinc-950 text-zinc-300 pt-12 pb-8 border-t border-zinc-800 text-xs">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-10">
        {/* FAQ Section */}
        <div className="space-y-4">
          <h2 className="text-base font-extrabold text-white flex items-center gap-2">
            <HelpCircle className="w-5 h-5 text-emerald-400" />
            Frequently Asked Questions &amp; UK Tax Guides
          </h2>

          <div className="space-y-2">
            {faqs.map((faq, idx) => (
              <div
                key={idx}
                className="border border-zinc-800 rounded-xl overflow-hidden bg-zinc-900/80 transition-colors"
              >
                <button
                  type="button"
                  onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                  className="w-full text-left p-3.5 font-semibold text-zinc-200 flex items-center justify-between gap-3 hover:text-white"
                >
                  <span>{faq.q}</span>
                  {openFaq === idx ? (
                    <ChevronUp className="w-4 h-4 text-emerald-400 shrink-0" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-zinc-500 shrink-0" />
                  )}
                </button>

                {openFaq === idx && (
                  <div className="p-3.5 pt-0 text-zinc-400 leading-relaxed border-t border-zinc-800/80 text-xs">
                    {faq.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Informational Content & HMRC Official Links */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-6 pt-6 border-t border-zinc-800 text-zinc-400">
          <div className="space-y-2">
            <h3 className="font-bold text-white text-sm">About Take Home Pay Calculators</h3>
            <p className="leading-relaxed">
              Designed to mirror <strong className="text-zinc-200">uktakehomepaycalculator.com</strong> with exact HMRC tax algorithms, NHS Pay Bands, Scottish tax rates, IR35 Net Pay, National Insurance Class 1 rates, workplace pensions, student loan thresholds, and tax codes for 2024/25 and 2025/26.
            </p>
          </div>

          <div className="space-y-2 md:col-span-2">
            <h3 className="font-bold text-white text-sm">Calculators</h3>
            <ul className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
              <li><Link to={ROUTES.calculator} className="hover:text-emerald-400 transition-colors">Gross to Net</Link></li>
              <li><Link to={ROUTES.monthly} className="hover:text-emerald-400 transition-colors">Monthly Take-Home</Link></li>
              <li><Link to={ROUTES.nhs} className="hover:text-emerald-400 transition-colors">NHS Salary</Link></li>
              <li><Link to={ROUTES.nurse} className="hover:text-emerald-400 transition-colors">Nurse Pay</Link></li>
              <li><Link to={ROUTES.scottish} className="hover:text-emerald-400 transition-colors">Scottish Tax</Link></li>
              <li><Link to={ROUTES.ir35} className="hover:text-emerald-400 transition-colors">IR35 Contractor</Link></li>
              <li><Link to={ROUTES.contractor} className="hover:text-emerald-400 transition-colors">Contractor Pay</Link></li>
              <li><Link to={ROUTES.umbrella} className="hover:text-emerald-400 transition-colors">Umbrella Pay</Link></li>
              <li><Link to={ROUTES.pension} className="hover:text-emerald-400 transition-colors">Salary Sacrifice</Link></li>
              <li><Link to={ROUTES.reverse} className="hover:text-emerald-400 transition-colors">Net to Gross</Link></li>
              <li><Link to={ROUTES.comparison} className="hover:text-emerald-400 transition-colors">Compare Pay</Link></li>
              <li><Link to={ROUTES.taxcode} className="hover:text-emerald-400 transition-colors">Tax Code Decoder</Link></li>
              <li><Link to={ROUTES.selfEmployed} className="hover:text-emerald-400 transition-colors">Self-Employed</Link></li>
              <li><Link to={ROUTES.bonus} className="hover:text-emerald-400 transition-colors">Bonus Tax</Link></li>
              <li><Link to={ROUTES.hourlyToSalary} className="hover:text-emerald-400 transition-colors">Hourly to Salary</Link></li>
              <li><Link to={ROUTES.proRata} className="hover:text-emerald-400 transition-colors">Pro Rata Salary</Link></li>
              <li><Link to={ROUTES.overtime} className="hover:text-emerald-400 transition-colors">Overtime Pay</Link></li>
              <li><Link to={ROUTES.minimumWage} className="hover:text-emerald-400 transition-colors">Minimum Wage</Link></li>
              <li><Link to={ROUTES.dividendTax} className="hover:text-emerald-400 transition-colors">Dividend Tax</Link></li>
              <li><Link to={ROUTES.mortgage} className="hover:text-emerald-400 transition-colors">Mortgage Repayments</Link></li>
              <li><Link to={ROUTES.stampDuty} className="hover:text-emerald-400 transition-colors">Stamp Duty</Link></li>
              <li><Link to={ROUTES.childBenefit} className="hover:text-emerald-400 transition-colors">Child Benefit</Link></li>
              <li><Link to={ROUTES.statutorySickPay} className="hover:text-emerald-400 transition-colors">Statutory Sick Pay</Link></li>
              <li><Link to={ROUTES.maternityPay} className="hover:text-emerald-400 transition-colors">Maternity Pay</Link></li>
              <li><Link to={ROUTES.teacherPay} className="hover:text-emerald-400 transition-colors">Teacher Salary</Link></li>
              <li><Link to={ROUTES.taxFreeChildcare} className="hover:text-emerald-400 transition-colors">Tax-Free Childcare</Link></li>
            </ul>
          </div>

          <div className="space-y-2">
            <h3 className="font-bold text-white text-sm">Official GOV.UK Sources</h3>
            <ul className="space-y-1.5 text-xs">
              <li>
                <a
                  href="https://www.gov.uk/income-tax-rates"
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 hover:text-emerald-400 text-zinc-300 underline underline-offset-2 decoration-zinc-600 hover:decoration-emerald-400"
                >
                  <span>GOV.UK Income Tax Rates &amp; Bands</span>
                  <ExternalLink className="w-3 h-3 text-emerald-400" />
                </a>
              </li>
              <li>
                <a
                  href="https://www.gov.uk/national-insurance-rates-letters"
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 hover:text-emerald-400 text-zinc-300 underline underline-offset-2 decoration-zinc-600 hover:decoration-emerald-400"
                >
                  <span>GOV.UK National Insurance Rates</span>
                  <ExternalLink className="w-3 h-3 text-emerald-400" />
                </a>
              </li>
              <li>
                <a
                  href="https://www.gov.uk/repaying-your-student-loan"
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 hover:text-emerald-400 text-zinc-300 underline underline-offset-2 decoration-zinc-600 hover:decoration-emerald-400"
                >
                  <span>GOV.UK Student Loan Thresholds</span>
                  <ExternalLink className="w-3 h-3 text-emerald-400" />
                </a>
              </li>
            </ul>
          </div>

          <div className="space-y-2">
            <h3 className="font-bold text-white text-sm">Disclaimer</h3>
            <p className="text-[11px] leading-relaxed text-zinc-400">
              This calculator provides estimates for illustrative and planning purposes based on standard UK HMRC regulations. Actual payroll calculations may vary slightly depending on exact employer payroll software rounding, cumulative tax code adjustments, or custom benefits in kind.
            </p>
          </div>
        </div>

        {/* Site Pages & Legal Navigation Row */}
        <div className="border-t border-zinc-800 pt-6 flex flex-wrap items-center justify-between gap-4 text-xs text-zinc-400">
          <div className="flex flex-wrap items-center gap-4 sm:gap-6 font-semibold">
            <button
              onClick={() => onNavigateToTab && onNavigateToTab('about')}
              className="hover:text-emerald-400 transition-colors"
            >
              About Us
            </button>
            <button
              onClick={() => onNavigateToTab && onNavigateToTab('write-for-us')}
              className="hover:text-emerald-400 transition-colors"
            >
              Write For Us
            </button>
            <button
              onClick={() => onNavigateToTab && onNavigateToTab('contact')}
              className="hover:text-emerald-400 transition-colors"
            >
              Contact Us
            </button>
            <button
              onClick={() => onNavigateToTab && onNavigateToTab('privacy')}
              className="hover:text-emerald-400 transition-colors"
            >
              Privacy Policy
            </button>
            <button
              onClick={() => onNavigateToTab && onNavigateToTab('terms')}
              className="hover:text-emerald-400 transition-colors"
            >
              Terms &amp; Conditions
            </button>
          </div>

          <div className="text-zinc-500 text-[11px]">
            © {new Date().getFullYear()} Take Home Pay Calculators UK. All rights reserved.
          </div>
        </div>
      </div>
    </footer>
  );
};

